import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { SERVER_URL } from "../../../constants";
import { AlertCircle, Plus, Loader, MapPin, Check, Phone } from "lucide-react";
import { useUser } from "@clerk/clerk-react";

const AddressTab = ({ addresses, setAddresses, selectedAddressId, setSelectedAddressId }) => {
  const { user } = useUser();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true); // Changed to true to handle initial load
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [newAddress, setNewAddress] = useState({
    userId: user.id,
    name: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    email: "",
    phone: "",
    notes: "",
  });

  const [validationErrors, setValidationErrors] = useState({});

  // Automatically select the address if there's only one
  useEffect(() => {
    if (addresses.length === 1 && !selectedAddressId) {
      setSelectedAddressId(addresses[0]._id);
    }
    if (addresses.length === 0 && initialLoad) {
      setIsModalOpen(true);
      
    }
  }, [addresses, selectedAddressId, setSelectedAddressId, initialLoad]);

  const validateForm = () => {
    const errors = {};
    if (!newAddress.email.trim()) errors.email = "Email is required";
    if (!newAddress.name.trim()) errors.name = "Name is required";
    if (!newAddress.address.trim()) errors.address = "Address is required";
    if (!newAddress.city.trim()) errors.city = "City is required";
    if (!newAddress.state.trim()) errors.state = "State is required";
    if (!newAddress.pincode.trim()) errors.pincode = "Pincode is required";
    if (!/^\d{6}$/.test(newAddress.pincode)) errors.pincode = "Pincode must be 6 digits";
    if (!newAddress.phone.trim()) {
      errors.phone = "Phone is required";
    } else if (!/^\d{10}$/.test(newAddress.phone)) {
      errors.phone = "Phone must be 10 digits";
    }
    if (!phoneVerified) errors.phone = "Phone number must be verified";

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;
    if (name === "phone") {
      formattedValue = value.replace(/\D/g, "").slice(0, 10);
      if (formattedValue !== newAddress.phone) {
        setPhoneVerified(false);
        setOtpSent(false);
      }
    } else if (name === "pincode") {
      formattedValue = value.replace(/\D/g, "").slice(0, 6);
    }

    setNewAddress((prev) => ({ ...prev, [name]: formattedValue }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSendOtp = async () => {
    if (!newAddress.phone || !/^\d{10}$/.test(newAddress.phone)) {
      setValidationErrors((prev) => ({
        ...prev,
        phone: "Valid 10-digit phone number is required to send OTP",
      }));
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${SERVER_URL}/api/v1/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: `+91${newAddress.phone}` }),
      });
      const result = await response.json();
      if (result.success) {
        setOtpSent(true);
        setIsOtpModalOpen(true);
        toast.success("OTP sent successfully!");
      } else {
        toast.error(result.message || "Failed to send OTP");
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      toast.error("An error occurred while sending OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      toast.error("Please enter a valid 6-digit OTP");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${SERVER_URL}/api/v1/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: `+91${newAddress.phone}`, otp }),
      });
      const result = await response.json();
      if (result.success) {
        setPhoneVerified(true);
        setIsOtpModalOpen(false);
        setOtp("");
        toast.success("Phone number verified successfully!");
      } else {
        toast.error(result.message || "Invalid OTP");
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      toast.error("An error occurred while verifying OTP");
    } finally {
      setLoading(false);
    }
  };

  const addNewAddress = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      const response = await fetch(`${SERVER_URL}/api/v1/address`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newAddress),
      });

      const result = await response.json();

      if (result.success) {
        const newAddressList = [...addresses, result.address];
        setAddresses(newAddressList);
        if (newAddressList.length === 1) {
          setSelectedAddressId(result.address._id); // Auto-select if it's the only address
        }
        toast.success("Address added successfully!");
        setNewAddress({
          userId: user.id,
          name: "",
          address: "",
          city: "",
          state: "",
          pincode: "",
          email: "",
          phone: "",
          notes: "",
        });
        setPhoneVerified(false);
        setOtpSent(false);
        setIsModalOpen(false);
      } else {
        toast.error(result.message || "Failed to add address");
      }
    } catch (error) {
      console.error("Error adding address:", error);
      toast.error("An error occurred while adding the address");
    } finally {
      setLoading(false);
    }
  };

  const handleEditAddress = (address) => {
    setNewAddress({
      userId: user.id,
      name: address.name,
      address: address.address,
      city: address.city,
      state: address.state,
      pincode: address.pincode,
      email: address.email,
      phone: address.phone,
      notes: address.notes,
    });
    setPhoneVerified(true); // Assume existing phone is verified
    setOtpSent(false);
    setIsModalOpen(true);
  };

  const EmptyState = () => (
    <div className="text-center py-12">
      <MapPin className="mx-auto h-12 w-12 text-gray-400" />
      <h3 className="mt-2 text-lg font-semibold text-gray-800">No addresses found</h3>
      <p className="mt-1 text-gray-500">Get started by adding a new address</p>
      <button
        onClick={() => setIsModalOpen(true)}
        className="mt-6 px-4 py-2 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white rounded-md hover:from-indigo-600 hover:to-indigo-700 transition-all duration-200 shadow-md"
      >
        <Plus className="mr-2 h-4 w-4 inline" /> Add New Address
      </button>
    </div>
  );

  const AddressCard = ({ address }) => (
    <div
      className={`p-4 border rounded-md transition-all cursor-pointer hover:border-indigo-500 hover:shadow-md ${
        selectedAddressId === address._id ? "border-2 border-indigo-500 shadow-lg" : ""
      }`}
      onClick={() => setSelectedAddressId(address._id)} // Select address on click
    >
      <div className="flex items-start gap-3">
        {addresses.length > 1 && (
          <input
            type="radio"
            name="address"
            checked={selectedAddressId === address._id}
            onChange={() => setSelectedAddressId(address._id)}
            className="mt-1 h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
          />
        )}
        <div className="flex-grow space-y-2">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold text-gray-800">{address.name}</h3>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleEditAddress(address);
              }}
              className="text-indigo-500 hover:text-indigo-600 text-sm"
            >
              Edit
            </button>
          </div>
          <p className="text-sm text-gray-600">{address.address}</p>
          <p className="text-sm text-gray-600">
            {address.city}, {address.state} - {address.pincode}
          </p>
          <p className="text-sm text-gray-600">Phone: +91 {address.phone}</p>
          {address.notes && (
            <p className="text-sm text-gray-500 italic">{address.notes}</p>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-800">Delivery Address</h2>
        {addresses.length > 0 && (
          <button
            onClick={() => setIsModalOpen(true)}
            className="px-4 py-2 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white rounded-md hover:from-indigo-600 hover:to-indigo-700 transition-all duration-200 shadow-md"
          >
            <Plus className="mr-2 h-4 w-4 inline" /> Add New Address
          </button>
        )}
      </div>

      {addresses.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {addresses.map((address) => (
            <AddressCard key={address._id} address={address} />
          ))}
        </div>
      )}

      {/* Add/Edit Address Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 space-y-4 shadow-2xl">
            <h3 className="text-lg font-semibold text-gray-800">
              {newAddress.phone && phoneVerified ? "Edit Address" : "Add New Address"}
            </h3>
            <div>
              <input
                name="name"
                placeholder="Full Name"
                value={newAddress.name}
                onChange={handleInputChange}
                className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-200 ${
                  validationErrors.name ? "border-red-500" : "border-gray-300"
                }`}
              />
              {validationErrors.name && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.name}</p>
              )}
            </div>
            <div>
              <input
                name="email"
                placeholder="Email"
                value={newAddress.email}
                onChange={handleInputChange}
                className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-200 ${
                  validationErrors.email ? "border-red-500" : "border-gray-300"
                }`}
              />
              {validationErrors.email && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.email}</p>
              )}
            </div>

            <div>
              <textarea
                name="address"
                placeholder="Street Address"
                value={newAddress.address}
                onChange={handleInputChange}
                className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-200 ${
                  validationErrors.address ? "border-red-500" : "border-gray-300"
                }`}
              />
              {validationErrors.address && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.address}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <input
                  name="city"
                  placeholder="City"
                  value={newAddress.city}
                  onChange={handleInputChange}
                  className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-200 ${
                    validationErrors.city ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {validationErrors.city && (
                  <p className="text-red-500 text-sm mt-1">{validationErrors.city}</p>
                )}
              </div>
              <div>
                <input
                  name="state"
                  placeholder="State"
                  value={newAddress.state}
                  onChange={handleInputChange}
                  className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-200 ${
                    validationErrors.state ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {validationErrors.state && (
                  <p className="text-red-500 text-sm mt-1">{validationErrors.state}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <input
                  name="pincode"
                  placeholder="Pincode"
                  value={newAddress.pincode}
                  onChange={handleInputChange}
                  className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-200 ${
                    validationErrors.pincode ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {validationErrors.pincode && (
                  <p className="text-red-500 text-sm mt-1">{validationErrors.pincode}</p>
                )}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-2 bg-gray-100 border border-gray-200 rounded-l text-gray-600">
                    +91
                  </span>
                  <input
                    name="phone"
                    placeholder="Phone Number"
                    value={newAddress.phone}
                    onChange={handleInputChange}
                    className={`flex-1 p-2 border rounded-r focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-200 ${
                      validationErrors.phone ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {!phoneVerified && (
                    <button
                      type="button"
                      onClick={handleSendOtp}
                      disabled={loading || otpSent}
                      className="px-3 py-2 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white rounded-md hover:from-indigo-600 hover:to-indigo-700 transition-all duration-200 shadow-md"
                    >
                      {loading ? (
                        <Loader className="h-4 w-4 inline animate-spin" />
                      ) : (
                        <Phone className="h-4 w-4 inline" />
                      )}
                      <span className="ml-1">{otpSent ? "OTP Sent" : "Send OTP"}</span>
                    </button>
                  )}
                </div>
                {validationErrors.phone && (
                  <p className="text-red-500 text-sm mt-1">{validationErrors.phone}</p>
                )}
                {phoneVerified && (
                  <p className="text-green-500 text-sm mt-1 flex items-center gap-1">
                    <Check className="h-4 w-4" /> Verified
                  </p>
                )}
              </div>
            </div>

            <textarea
              name="notes"
              placeholder="Delivery Notes (Optional)"
              value={newAddress.notes}
              onChange={handleInputChange}
              rows="3"
              className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-200 border-gray-300"
            />

            <div className="flex justify-end gap-4">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300 transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={addNewAddress}
                disabled={loading || !phoneVerified}
                className="px-4 py-2 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white rounded-md hover:from-indigo-600 hover:to-indigo-700 transition-all duration-200 shadow-md disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed"
              >
                {loading && <Loader className="mr-2 h-4 w-4 inline animate-spin" />}
                Save Address
              </button>
            </div>
          </div>
        </div>
      )}

      {/* OTP Verification Modal */}
      {isOtpModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Verify Phone Number</h3>
            <p className="text-gray-600 mb-4">
              Enter the 6-digit OTP sent to +91 {newAddress.phone}
            </p>
            <input
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-200 border-gray-300"
              placeholder="Enter OTP"
            />
            <div className="flex justify-end gap-4 mt-4">
              <button
                onClick={() => setIsOtpModalOpen(false)}
                className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300 transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleVerifyOtp}
                disabled={loading}
                className="px-4 py-2 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white rounded-md hover:from-indigo-600 hover:to-indigo-700 transition-all duration-200 shadow-md"
              >
                {loading ? (
                  <Loader className="mr-2 h-4 w-4 inline animate-spin" />
                ) : (
                  <Check className="mr-2 h-4 w-4 inline" />
                )}
                Verify
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressTab;
